import React, { useState } from "react";
import { PortfolioProperty } from "../types/ownerPortfolio.types";
import { condominiumAccentClass } from "../utils/formatPortfolioDisplay";
import { getPortfolioPropertyText } from "../utils/getPortfolioPropertyText";
import { PortfolioPanelButtons, PortfolioPanels } from "./PortfolioPanels";
import { SITUATION_BADGE_CLASS } from "./situationStyles";

interface PortfolioPropertyCardProps {
  property: PortfolioProperty;
  accessToken: string | null;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="m-0 text-[0.65rem] uppercase tracking-wide text-[#6e7681]">{label}</p>
      <p className="m-0 mt-0.5 text-sm text-[#f0f6fc]">{value}</p>
    </div>
  );
}

export default function PortfolioPropertyCard({
  property,
  accessToken,
}: PortfolioPropertyCardProps) {
  const [insightOpen, setInsightOpen] = useState(false);
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const text = getPortfolioPropertyText(property);

  return (
    <article
      className={`rounded-2xl border border-white/[0.06] border-l-2 bg-[#1c2128] p-3 ${condominiumAccentClass(property.condominium_name)}`}
      data-testid={`portfolio-card-${property.kenlo_property_id || property.contract_code}`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="m-0 text-sm font-semibold text-[#f0f6fc]">{text.place.headline}</p>
          {text.place.condominium && (
            <p className="m-0 mt-1 text-xs text-[#8b949e]">{text.place.condominium}</p>
          )}
          <p className="m-0 mt-1 text-xs text-[#8b949e]">
            Unidade {text.unit} · Contrato {text.contract}
          </p>
          {text.place.address && <p className="m-0 mt-1 text-xs text-[#8b949e]">{text.place.address}</p>}
        </div>
        <span
          className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[0.65rem] font-semibold leading-none ${SITUATION_BADGE_CLASS[property.situation]}`}
        >
          {text.situation}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Fact label="Aluguel" value={text.rent} />
        <Fact label="Proprietário" value={text.owner} />
        <Fact label="Metragem" value={text.area} />
        <Fact label="Início" value={text.contractStart} />
        <Fact label="Fim" value={text.contractEnd} />
      </div>

      {text.daysVacant && <p className="m-0 mt-3 text-xs text-[#8b949e]">{text.daysVacant}</p>}
      {property.diagnosis_label && (
        <p className="m-0 mt-1 text-xs text-[#8b949e]">{property.diagnosis_label}</p>
      )}

      <div className="mt-3 flex flex-col gap-3">
        <PortfolioPanelButtons
          property={property}
          insightOpen={insightOpen}
          inspectionOpen={inspectionOpen}
          onToggleInsight={() => {
            setInsightOpen((open) => !open);
            setInspectionOpen(false);
          }}
          onToggleInspection={() => {
            setInspectionOpen((open) => !open);
            setInsightOpen(false);
          }}
        />
        <PortfolioPanels
          property={property}
          accessToken={accessToken}
          insightOpen={insightOpen}
          inspectionOpen={inspectionOpen}
          onCloseInspection={() => setInspectionOpen(false)}
          onCloseInsight={() => setInsightOpen(false)}
        />
      </div>
    </article>
  );
}
